import React from 'react';
import { ArrowRightCircle, Clock3, PauseCircle, ShieldCheck, UserCheck } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import SimButton from '../components/ui/SimButton';
import api from '../services/api';
import SimCard from '../sim/ui/SimCard';
import SimShell from '../sim/ui/SimShell';
import { SimBody, SimLabel, SimTitle } from '../sim/ui/SimText';

type StartAttemptResponse = {
  attempt_id: string;
  variant_code: string;
  first_screen_code: string;
  next_url: string;
};

const SimLinkEntryPage: React.FC = () => {
  const navigate = useNavigate();
  const { linkToken } = useParams();
  const [fullName, setFullName] = React.useState('');
  const [saIdNumber, setSaIdNumber] = React.useState('');
  const [acceptedTerms, setAcceptedTerms] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const canSubmit =
    Boolean(linkToken && linkToken.trim().length > 0) &&
    fullName.trim().length >= 2 &&
    /^\d{13}$/.test(saIdNumber.trim()) &&
    acceptedTerms &&
    !submitting;

  const handleStart = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!linkToken) {
      setErrorMessage('Missing or invalid assessment link.');
      return;
    }
    if (!acceptedTerms) {
      setErrorMessage('You must agree to the Terms of Service and Privacy Policy before starting.');
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);
    try {
      const response = await api.post<StartAttemptResponse>('/sim/attempts/start', {
        link_token: linkToken,
        candidate: {
          full_name: fullName.trim(),
          sa_id_number: saIdNumber.trim(),
          accepted_terms: true,
          terms_version: 'v1',
        },
      });
      navigate(response.data.next_url);
    } catch (error: any) {
      const message =
        error?.response?.data?.error?.message || 'Unable to start assessment. Please try again.';
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-page">
      <nav
        style={{
          background: '#0d9488',
          height: '52px',
          display: 'flex',
          alignItems: 'center',
          padding: '0 28px',
          gap: '10px',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: '32px',
            height: '32px',
            background: 'rgba(255,255,255,0.18)',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 700,
            fontSize: '18px',
            color: '#fff',
          }}
        >
          I
        </div>
        <span
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 700,
            fontSize: '22px',
            color: '#fff',
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
          }}
        >
          Intellerview
        </span>
      </nav>

      <main>
        <SimShell maxWidthClassName="max-w-3xl" contentClassName="space-y-6" className="min-h-0">
          <SimCard className="relative overflow-hidden">
            <div className="space-y-4">
              <SimLabel className="break-words text-emerald-300">Simulation Access</SimLabel>
              <SimTitle>Role Simulation Entry</SimTitle>
              <SimBody>Please review the instructions below before you begin.</SimBody>
            </div>
            <div className="mt-6 border-t border-white/10 pt-4">
              <SimLabel>Assessment Reference</SimLabel>
              <p className="mt-2 text-sm leading-tight text-txt-secondary">Provided after start</p>
            </div>
          </SimCard>

          <div className="grid gap-4 sm:gap-5 md:grid-cols-2">
            <SimCard className="space-y-4">
              <h2 className="text-lg font-semibold text-txt-primary">Before you start</h2>
              <ul className="space-y-3 text-sm text-txt-secondary">
                <li className="flex items-start gap-3">
                  <Clock3 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" aria-hidden="true" />
                  <span>Allow ~60 minutes where you won’t be interrupted.</span>
                </li>
                <li className="flex items-start gap-3">
                  <PauseCircle className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" aria-hidden="true" />
                  <span>No pause or save: once started, you must complete in one sitting.</span>
                </li>
                <li className="flex items-start gap-3">
                  <UserCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" aria-hidden="true" />
                  <span>One attempt only: this assessment is linked to your name and SA ID number.</span>
                </li>
                <li className="flex items-start gap-3">
                  <ArrowRightCircle className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" aria-hidden="true" />
                  <span>Global timing: the full simulation runs on a single timer and you must proceed forward.</span>
                </li>
              </ul>
            </SimCard>

            <SimCard className="space-y-4">
              <h2 className="text-lg font-semibold text-txt-primary">Integrity monitoring</h2>
              <div className="flex items-start gap-3">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" aria-hidden="true" />
                <p className="text-sm leading-relaxed text-txt-secondary">
                  This assessment includes integrity monitoring. We track behavioural patterns including tab
                  switching, idle patterns, paste events, and others to detect use of external tools (including AI).
                  Your employer receives both your score and an integrity confidence rating (High / Medium / Low).
                </p>
              </div>
            </SimCard>
          </div>

          <SimCard>
            <form onSubmit={handleStart} className="space-y-5">
              <div className="space-y-2">
                <label className="text-sm font-medium text-txt-secondary">Full name</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(event) => setFullName(event.target.value)}
                  placeholder="Jane Candidate"
                  className="w-full rounded-input border border-line bg-surface-page/60 px-4 py-3 text-txt-primary placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-txt-secondary">SA ID number</label>
                <input
                  type="text"
                  value={saIdNumber}
                  onChange={(event) => setSaIdNumber(event.target.value.replace(/\D/g, '').slice(0, 13))}
                  inputMode="numeric"
                  placeholder="8001015009087"
                  className="w-full rounded-input border border-line bg-surface-page/60 px-4 py-3 text-txt-primary placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400"
                />
              </div>

              <label className="flex items-start gap-3 text-sm text-txt-secondary">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(event) => {
                    setAcceptedTerms(event.target.checked);
                    if (event.target.checked) {
                      setErrorMessage(null);
                    }
                  }}
                  className="mt-0.5 h-4 w-4 rounded border-line bg-surface-card text-emerald-400 focus:ring-2 focus:ring-emerald-400"
                />
                <span>
                  I agree to the{' '}
                  <a
                    href="/legal/term-of-service.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-600 underline hover:text-brand-700"
                  >
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a
                    href="/legal/privacy-policy.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-600 underline hover:text-brand-700"
                  >
                    Privacy Policy
                  </a>
                  .
                </span>
              </label>

              {errorMessage && (
                <div className="rounded-input border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
                  {errorMessage}
                </div>
              )}

              <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <SimButton variant="secondary" onClick={() => navigate('/')} className="w-full sm:w-auto">
                  Exit
                </SimButton>
                <SimButton type="submit" disabled={!canSubmit} className="w-full sm:w-auto">
                  {submitting ? 'Starting assessment...' : 'Start assessment'}
                </SimButton>
              </div>
            </form>
          </SimCard>
        </SimShell>
      </main>
    </div>
  );
};

export default SimLinkEntryPage;
